import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './hooks/useAuth';
import { CircularProgress } from '@mui/material';
import { ColorSchemeProvider } from './context/ColorSchemeContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeModeContext';
import { NotificationsProvider } from './context/NotificationsContext';
import RealTimeProvider from './components/RealTimeProvider';
import DashboardLayout from './components/layouts/DashboardLayout';
import PublicLayout from './components/layouts/PublicLayout';
import LandingLayout from './components/layouts/LandingLayout';
import Login from './pages/Login';
import HomePage from './pages/HomePage';
import DashboardHome from './pages/DashboardHome';
import UserManagement from './pages/admin/UserManagement';
import BrandManagement from './pages/admin/BrandManagement';
import SuccessStoryManagement from './pages/admin/SuccessStoryManagement';
import FormSubmissionManagement from './pages/admin/FormSubmissionManagement';
import ABTestCalculator from './pages/ABTestCalculator';
import NotFound from './pages/NotFound';
import ReportDetail from './pages/ReportDetail';
import SharedReport from './pages/SharedReport';
import Profile from './pages/Profile';
import DashboardStatistics from './pages/DashboardStatistics';
import StakeholderPortal from './pages/StakeholderPortal';
import BrandPage from './pages/BrandPage';
import TestLibraryPage from './pages/TestLibraryPage';
import ReportCalculationGuide from './pages/ReportCalculationGuide';
import CalculationMethodsGuidePage from './pages/CalculationMethodsGuidePage';
import { createBrandTheme, BRAND } from './theme/brandTheme';
import './styles/dashboard-shell.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: BRAND.purple }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const fallback =
      user.role === 'stakeholder' ? '/dashboard/stakeholder' : '/dashboard';
    return <Navigate to={fallback} />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route element={<LandingLayout />}>
        <Route index element={<HomePage />} />
        <Route path="calculator" element={<ABTestCalculator layout="public" />} />
        <Route path="methods-guide" element={<CalculationMethodsGuidePage />} />
        <Route path="calculation-guide" element={<ReportCalculationGuide />} />
      </Route>

      <Route element={<PublicLayout />}>
        <Route path="login" element={<Login />} />
        <Route path="shared/:token" element={<SharedReport />} />
      </Route>

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="stakeholder" element={<StakeholderPortal />} />
        <Route path="statistics" element={<DashboardStatistics />} />
        <Route path="brand/:brandId" element={<BrandPage />} />
        <Route
          path="library"
          element={
            <ProtectedRoute allowedRoles={['admin', 'editor', 'user']}>
              <TestLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route path="reports/:id" element={<ReportDetail />} />
        <Route
          path="calculator"
          element={
            <ProtectedRoute allowedRoles={['admin', 'editor', 'user']}>
              <ABTestCalculator />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<Profile />} />
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="brands"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BrandManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="success-stories"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SuccessStoryManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="form-submissions"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FormSubmissionManagement />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/** Inner app — reads theme mode and builds MUI theme dynamically */
function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = useMemo(() => createTheme(createBrandTheme(mode)), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ColorSchemeProvider initialScheme="default">
        <NotificationsProvider>
          <RealTimeProvider>
            <AppRoutes />
          </RealTimeProvider>
        </NotificationsProvider>
      </ColorSchemeProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;
